import { useState } from "react";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import {
  ApiResponse,
  DirectorInformationRequestBody,
  MappedAppointments,
  OtherEntityAppointment,
  Steps,
  DirectorFormData,
} from "./types/director";

const useDirectors = (steps: Steps[] = []) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isStepValid, setIsStepValid] = useState(false);
  const [cancelAppointment, setCancelAppointment] = useState(false);
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [appointments, setAppointments] = useState<MappedAppointments[] | []>(
    [],
  );
  const [isDirectorRegistered, setIsDirectorRegistered] = useState(false);
  const [editAppointmentEntry, setEditAppointmentEntry] =
    useState<MappedAppointments | null>(null);
  const [isEditAppointment, setIsEditAppointment] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registeredDirector, setRegisteredDirector] = useState(null);
  const [otherEntitiesAppointments, setOtherEntitiesAppointments] = useState<
    OtherEntityAppointment[]
  >([]);
  const [showDeleteDirectorModal, setShowDeleteDirectorModal] = useState(false);
  const [isAuditSidebarVisible, setIsAuditSidebarVisible] = useState(false);
  const fetchEntityDetails = async (id: string) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.FETCH_ENTITY,
        "GET",
        {
          replace: { id },
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to fetch entity details");
    }
  };

  const handleBack = () => {
    setActiveIndex((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
  };

  const handleNext = () => {
    setActiveIndex((prevActiveStep) =>
      Math.min(prevActiveStep + 1, steps.length - 1),
    );
  };

  const showCancelModal = () => {
    setCancelAppointment(true);
  };

  const updateDirectorInformation = async (
    directorId: string,
    requestBody: Partial<DirectorInformationRequestBody>,
  ) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.UPDATE_DIRECTOR,
        "PUT",
        {
          replace: { id: directorId },
          body: JSON.stringify(requestBody),
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to update Director Information");
    }
  };

  const fetchDirectorAppointmentHistory = async (
    directorId: string,
    entityId?: string,
  ) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.DIRECTOR_APPOINTMENT_HISTORY,
        "GET",
        {
          skipCache: true,
          replace: { directorId, entityId: entityId || "" },
        },
      );
      if (response.success) {
        setAppointments(response.data);
      }
      return response;
    } catch (error) {
      throw new Error("Failed to fetch director appointment history");
    }
  };

  const addDirectorAppointmentHistory = async (
    directorId: string,
    requestBody: Partial<DirectorInformationRequestBody>,
  ) => {
    try {
      if (!directorId) return;
      const entityId = (requestBody as any)?.entity_id || "";
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.DIRECTOR_APPOINTMENT_HISTORY,
        "POST",
        {
          replace: { directorId, entityId },
          body: JSON.stringify(requestBody),
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to add director appointment history");
    }
  };

  const updateDirectorAppointmentHistory = async (
    directorId: string,
    requestBody: Partial<DirectorInformationRequestBody>,
    appointmentId: string,
  ) => {
    try {
      if (!appointmentId) return;
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.UPDATE_DIRECTOR_APPOINTMENT,
        "PUT",
        {
          replace: { directorId, appointmentId },
          body: JSON.stringify(requestBody),
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to update director appointment history");
    }
  };

  const deleteDirectorAppointmentHistory = async (
    directorId: string,
    appointmentId: string,
  ) => {
    try {
      if (!appointmentId) return;
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.UPDATE_DIRECTOR_APPOINTMENT,
        "DELETE",
        {
          replace: { directorId, appointmentId },
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to delete director appointment history");
    }
  };

  const fetchOtherEntitiesAppointments = async (
    directorId: string,
    currentEntityId?: string,
  ) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.OTHER_ENTITIES_APPOINTMENTS,
        "GET",
        {
          skipCache: true,
          replace: { directorId, entityId: currentEntityId || "" },
        },
      );
      if (response.success) {
        // Filter out appointments from the current entity to get only "other entities" appointments
        const allAppointments = response.data || [];
        const otherAppointments = currentEntityId
          ? allAppointments.filter(
              (appointment: OtherEntityAppointment) =>
                appointment.entity_id !== currentEntityId,
            )
          : allAppointments;
        setOtherEntitiesAppointments(otherAppointments);
      }
      return response;
    } catch (error) {
      throw new Error("Failed to fetch other entities appointments");
    }
  };

  const mapDirectorPayload = (data: DirectorFormData, entityId: string) => ({
    name: data.given_name,
    other_name: data.other_name || undefined,
    last_name: data.surname || undefined,
    preferred_name: data.preferred_name || undefined,
    email: data.email || undefined,
    phone: data.phone_number ? Number(data.phone_number) : undefined,
    phone_code: data.phone_code ? Number(data.phone_code) : undefined,

    positions: data.positions?.map((p) => ({
      start_date: p.start_date,
      end_date: p.end_date,
      title: p.title,
      position: p.position,
      role: p.role,
      status: p.status,
      entity_id: p.entity_id || entityId,
    })),
    social_security_number: data.social_security_number || undefined,
    passport_id: data.passport_number || undefined,

    country_of_issue:
      typeof data.country_of_issue === "string"
        ? data.country_of_issue
        : data.country_of_issue?.code,

    nationality:
      typeof data.nationality === "string"
        ? data.nationality
        : data.nationality?.code,

    date_of_birth: data.date_of_birth || undefined,
    expiry_date: data.expiry_date || undefined,

    director_address: {
      type: "Registered",
      country:
        typeof data.address_country === "string"
          ? data.address_country
          : data.address_country?.code,

      address_line_1: data.address_line_1,
      address_line_2: data.address_line_2 || undefined,

      state:
        typeof data.address_state === "string"
          ? data.address_state
          : data.address_state?.code,

      city:
        typeof data.address_city === "string"
          ? data.address_city
          : data.address_city?.code,

      postalcode: data.address_postal_code,
    },

    entities: [entityId],
  });

  const submitDirector = async (data: DirectorFormData, entityId: string) => {
    try {
      setIsSubmitting(true);
      const payload = mapDirectorPayload(data, entityId);
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.ADD_DIRECTOR,
        "POST",
        { body: payload },
      );
      setRegisteredDirector(response);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteDirector = async (directorId: string) => {
    try {
      if (!directorId) return;
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.DELETE_DIRECTOR,
        "DELETE",
        {
          replace: { directorId },
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to delete director");
    }
  };
  return {
    fetchEntityDetails,
    activeIndex,
    setActiveIndex,
    handleBack,
    handleNext,
    isStepValid,
    setIsStepValid,
    cancelAppointment,
    setCancelAppointment,
    showCancelModal,
    showAddAppointmentModal,
    setShowAddAppointmentModal,
    appointments,
    setAppointments,
    editAppointmentEntry,
    setEditAppointmentEntry,
    isEditAppointment,
    setIsEditAppointment,
    updateDirectorInformation,
    fetchDirectorAppointmentHistory,
    addDirectorAppointmentHistory,
    updateDirectorAppointmentHistory,
    deleteDirectorAppointmentHistory,
    showDeleteModal,
    setShowDeleteModal,
    submitDirector,
    isSubmitting,
    isDirectorRegistered,
    setIsDirectorRegistered,
    registeredDirector,
    otherEntitiesAppointments,
    setOtherEntitiesAppointments,
    fetchOtherEntitiesAppointments,
    showDeleteDirectorModal,
    setShowDeleteDirectorModal,
    deleteDirector,
    isAuditSidebarVisible,
    setIsAuditSidebarVisible,
  };
};
export default useDirectors;
